import React, { useContext, useEffect } from 'react';
import {
  Navigate,
  Routes,
  Route,
  useParams,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import classNames from 'classnames';

import Home from 'containers/user/home/home';
import Reports from 'containers/user/reports/reports';
import Profile from 'containers/admin/profile/profile';
import Files from 'containers/user/files/files';
import ProfileForUserMode from 'containers/user/profile/profile';
import Learn from 'containers/user/learn/learn';
import ReportPage from 'containers/user/reports/report/report';

import GetStartedGuide from 'containers/user/learn/components/get-started-guide/get-started-guide';

import Content from 'containers/user/learn/containers/content/content';
import Articles from 'containers/user/learn/containers/content/articles/articles';
import Article from 'containers/user/learn/containers/content/articles/article/article';
import Downloads from 'containers/user/learn/containers/content/downloads/downloads';
import Download from 'containers/user/learn/containers/content/downloads/download/download';
import Guides from 'containers/user/learn/containers/content/guides/guides';
import Guide from 'containers/user/learn/containers/content/guides/guide/guide';
import Webinars from 'containers/user/learn/containers/content/webinars/webinars';
import Webinar from 'containers/user/learn/containers/content/webinars/webinar/webinar';
import Courses from 'containers/user/learn/containers/content/cources/courses';
import CourseOverview from 'containers/user/learn/containers/content/cources/cource-overview/course-overview';

import ClientsAdminPage from 'containers/admin/clients/clients-admin-page';
import ClientDetails from 'containers/admin/clients/client/client-details';
import ClientProfile from 'containers/admin/clients/client/client-profile/client-profile';

import UsersAdminPage from 'containers/admin/users/users-admin-page';
import UserPage from 'containers/admin/users/user/user-page/user-page';
import UserProfile from 'containers/admin/users/user/user-profile/user-profile';

import ReportsAdminPage from 'containers/admin/reports/reports-admin.page';
import AdminReport from 'containers/admin/reports/report/report';

import ProductsAdminPage from 'containers/admin/products/products-admin-page';
import AdminProduct from 'containers/admin/products/product/product';

import FilePage from 'containers/admin/clients/client/tabs-components/files-tab/file/file-page/file-page';
import File from 'containers/admin/clients/client/tabs-components/files-tab/file/file';
import CourseDetails from 'containers/user/learn/containers/content/cources/course-details/course-details';

import GoalsManager from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goals-manager';

import Product from 'containers/admin/clients/client/tabs-components/products-tab/product/product';
import CriticalElementManager from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-manager/critical-element-manager';

import MyGoals from 'containers/user/goals/goals';

import { useImpersonation } from 'state/impersonation/impersonation.thunk';
import { FeatureFlagsContext } from 'app';

const withParams = (Component, paramMapping) => (props) => {
  const params = useParams();
  const paramProps = {};

  Object.entries(paramMapping).forEach(([propName, paramName]) => {
    paramProps[propName] = params[paramName || propName];
  });

  return <Component {...paramProps} {...props} />;
};

const withLocation = (Component) => (props) => {
  const location = useLocation();
  const state = location?.state;

  return (
    <Component notificationParams={state?.notificationParams} {...props} />
  );
};

const withLocationAndParams = (Component, paramMapping) => (props) => {
  const params = useParams();
  const location = useLocation();
  const state = location?.state;
  const paramProps = {};

  Object.entries(paramMapping).forEach(([propName, paramName]) => {
    paramProps[propName] = params[paramName || propName];
  });

  return (
    <Component
      notificationParams={state?.notificationParams}
      {...paramProps}
      {...props}
    />
  );
};

const EnhancedComponents = {
  ReportPage: withParams(ReportPage, { reportId: 'id' }),
  Article: withParams(Article, { articleId: 'id' }),
  Guide: withParams(Guide, { guideId: 'id' }),
  Download: withParams(Download, { downloadId: 'id' }),
  Webinar: withParams(Webinar, { webinarId: 'id' }),
  CourseOverview: withParams(CourseOverview, { courseId: 'id' }),
  AdminReport: withParams(AdminReport, { reportId: 'id' }),
  AdminProduct: withParams(AdminProduct, { productId: 'id' }),
  Product: withParams(Product, { productId: 'id', clientId: 'clientId' }),

  ClientsAdminPage: withLocation(ClientsAdminPage),
  UsersAdminPage: withLocation(UsersAdminPage),
  UserProfile: withLocation(UserProfile),

  UserPage: withLocationAndParams(UserPage, { clientId: 'clientId' }),
  UserPageProfile: withLocation(UserPage),
  FilePage: withLocationAndParams(FilePage, {
    clientId: 'clientId',
    fileId: 'fileId',
  }),
};

const ClientRedirect = () => {
  const { id } = useParams();

  return <Navigate replace to={`/admin/clients/${id}/users`} />;
};

const UserRedirect = () => {
  const { id } = useParams();

  return <Navigate replace to={`/admin/users/${id}/profile`} />;
};

const MainAuthenticated = ({ user }) => {
  const internalAdmin = user?.data?.internalAdmin;
  const isAdmin = user?.data && (user?.data?.fdUser || user?.data?.clientAdmin);
  const mvAnalyst = user?.data?.fdAnalyst;
  const { featureFlags } = useContext(FeatureFlagsContext);
  const { impersonatingUser } = useImpersonation();
  const navigate = useNavigate();

  useEffect(() => {
    if (window.location.pathname === '/') {
      if (isAdmin) {
        navigate('/admin/clients', { replace: true });
      } else {
        navigate('/home', { replace: true });
      }
    }
  }, [isAdmin, navigate]);

  const userRoutes = !isAdmin
    ? [
        { path: 'home', element: <Home /> },
        { path: 'reports/:id', element: <EnhancedComponents.ReportPage /> },
        { path: 'reports/:reportType?', element: <Reports /> },
        { path: 'reports', element: <Reports /> },
        { path: 'profile', element: <ProfileForUserMode /> },
        { path: 'learn', element: <Learn /> },
        { path: 'learn/get-started-guide', element: <GetStartedGuide /> },
        { path: 'learn/content', element: <Content /> },
        { path: 'learn/content/articles', element: <Articles /> },
        {
          path: 'learn/content/articles/:id',
          element: <EnhancedComponents.Article />,
        },
        { path: 'learn/content/guides', element: <Guides /> },
        {
          path: 'learn/content/guides/:id',
          element: <EnhancedComponents.Guide />,
        },
        { path: 'learn/content/downloads', element: <Downloads /> },
        {
          path: 'learn/content/downloads/:id',
          element: <EnhancedComponents.Download />,
        },
        { path: 'learn/content/webinars', element: <Webinars /> },
        {
          path: 'learn/content/webinars/:id',
          element: <EnhancedComponents.Webinar />,
        },
        { path: 'learn/content/glossary', element: <div>Glossary</div> },

        { path: 'learn/courses', element: <Courses /> },
        {
          path: 'learn/course/:id',
          element: <EnhancedComponents.CourseOverview />,
        },
        {
          path: 'learn/courses/:courseId/lesson/:lessonId',
          element: <CourseDetails />,
        },
        { path: 'files', element: <Files /> },
      ]
    : [];

  // User goals routes
  const goalsRoutes =
    featureFlags['tab-goals'] && !isAdmin
      ? [{ path: 'goals', element: <MyGoals /> }]
      : [];

  // Analyst only routes
  const adminFileRoutes = !mvAnalyst
    ? [{ path: 'admin/clients/:clientId/new-file', element: <File /> }]
    : [];

  // Admin routes
  const commonAdminRoutes = isAdmin
    ? [
        { path: 'admin/profile', element: <Profile /> },
        {
          path: 'admin/clients/:clientId/products/:id',
          element: <EnhancedComponents.Product />,
        },
        {
          path: 'admin/clients/:clientId/critical-elements/:year/:id',
          element: <CriticalElementManager />,
        },
        {
          path: 'admin/clients',
          element: <EnhancedComponents.ClientsAdminPage />,
        },
        {
          path: 'admin/clients/new',
          element: <ClientProfile pageName="Add new client" />,
        },
        {
          path: 'admin/clients/:clientId/users/:id',
          element: <EnhancedComponents.UserPage />,
        },
        {
          path: 'admin/clients/:clientId/files/:fileId',
          element: <EnhancedComponents.FilePage />,
        },
        {
          path: 'admin/clients/:clientId/goals/:year/:goalType/:goalCategory/:goalId?',
          element: <GoalsManager />,
        },
        {
          path: 'admin/clients/:clientId/users/:id/profile',
          element: <EnhancedComponents.UserPageProfile />,
        },
        { path: 'admin/clients/:id', element: <ClientRedirect /> },
        { path: 'admin/clients/:id/*', element: <ClientDetails /> },
        { path: 'admin/users', element: <EnhancedComponents.UsersAdminPage /> },
        {
          path: 'admin/users/new',
          element: <EnhancedComponents.UserProfile />,
        },
        { path: 'admin/users/:id', element: <UserRedirect /> },
        {
          path: 'admin/users/:id/profile',
          element: <EnhancedComponents.UserPageProfile />,
        },
      ]
    : [];

  // Super admin only routes
  const nonInternalAdminRoutes =
    isAdmin && !internalAdmin
      ? [
          { path: 'admin/reports', element: <ReportsAdminPage /> },
          {
            path: 'admin/reports/:id',
            element: <EnhancedComponents.AdminReport />,
          },
          { path: 'admin/products', element: <ProductsAdminPage /> },
          {
            path: 'admin/products/:id',
            element: <EnhancedComponents.AdminProduct />,
          },
        ]
      : [];

  const routes = [
    // Default route
    {
      path: '*',
      element: <Navigate replace to={isAdmin ? '/admin/clients' : '/home'} />,
    },
    ...userRoutes,
    ...goalsRoutes,
    ...adminFileRoutes,
    ...commonAdminRoutes,
    ...nonInternalAdminRoutes,
  ];

  return (
    <main
      className={classNames(
        'flex flex-col flex-auto mt-14 items-center w-full',
        {
          'mobile:!mt-[97px] tablet:!mt-[104px]': !isAdmin && impersonatingUser,
        }
      )}
    >
      <Routes>
        {routes.map((route, index) => (
          <Route
            key={`route-${index}`}
            path={route.path}
            element={route.element}
          />
        ))}
      </Routes>
    </main>
  );
};

export default MainAuthenticated;
